import apiRoutes from '@constants/apiRoutes';
import { DataAccessProvider } from '@constants/constants';
import baseApi from '@services/api';
import { DealItem, DealsCsvDownloadResponse, DealsListingResponse } from './types/games';
import { PaginatedResponse } from './types/common';

export interface GameDetailsPayload {
  name: string;
  dealName: string;
  companyName: string;
  refreshRequired: boolean;
  clientCode: string;
  signingAuthority: string | null;
  signingAuthorityEmail: string | null;
  signingAuthorityTitle: string | null;
  address: string | null;
  genre: string | null;
  category: string | null;
  dataSource: DataAccessProvider;
  status: string | null;
  metadata: string | null;
  targetIrr: number | null;
}

export interface GameDetailsResponse {
  data: GameDetails;
  code: number;
  message: string;
}

interface GameDetails {
  id: string;
  clientCode: string;
  gameId: string;
  name: string;
  dealName: string;
  companyName: string;
  address: string;
  signingAuthority: string;
  signingAuthorityEmail: string;
  signingAuthorityTitle: string;
  status: string; // Replace or extend union type if there are other possible statuses
  category: string;
  genre: string;
  metadata: string;
  refreshRequired: boolean;
  targetIrr: number;
}

const games = baseApi.injectEndpoints({
  endpoints: (build) => ({
    gameDetails: build.mutation<any, GameDetailsPayload>({
      query: (payload) => ({
        url: `${apiRoutes.games}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['companyList'],
    }),

    updateGameDetails: build.mutation<any, { gameId: string; body: GameDetailsPayload }>({
      query: (payload) => ({
        url: `${apiRoutes.games}/${payload.gameId}`,
        method: 'PUT',
        body: payload.body,
      }),
      invalidatesTags: ['companyList'],
    }),

    getGames: build.query<DealsListingResponse, string>({
      query: (queryString: string) => `${apiRoutes.games}${queryString ? `?${queryString}` : ''}`,
    }),

    getGamesPaginated: build.infiniteQuery<PaginatedResponse<DealItem>, string, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.totalPages === lastPageParam + 1) return undefined;
          return lastPageParam + 1;
        },
      },

      query: ({ queryArg, pageParam }) => {
        const base = `${apiRoutes.games}?`;
        return `${base}${queryArg || ''}&pageNumber=${pageParam}`;
      },
    }),

    getGameById: build.query<GameDetailsResponse, string>({
      query: (gameId: string) => `${apiRoutes.games}/${gameId}`,
    }),

    getCategoriesAndGenres: build.query<any, void>({
      query: () => `${apiRoutes.categoriesAndGenres}`,
    }),
    dealsCsvDownload: build.query<
      DealsCsvDownloadResponse,
      { refreshRequired?: boolean; search?: string }
    >({
      query: ({ refreshRequired, search }) => {
        const queryParams: string[] = [];

        queryParams.push(`sort=updatedAt`);
        queryParams.push(`sortOrder=DESC`);

        if (refreshRequired?.toString()) {
          queryParams.push(`refreshRequired=${refreshRequired}`);
        }

        if (search) {
          queryParams.push(`search=${encodeURIComponent(search)}`);
        }

        return apiRoutes.dealsCsvDownload(queryParams.join('&'));
      },
    }),
  }),
});

export const {
  useGetGamesPaginatedInfiniteQuery,
  useLazyGetGamesQuery,
  useGameDetailsMutation,
  useUpdateGameDetailsMutation,
  useLazyGetGameByIdQuery,
  useLazyGetCategoriesAndGenresQuery,
  useLazyDealsCsvDownloadQuery,
} = games;
